import { Offer, OfferStatusEnum } from "../../modules/shared/model/offer.interfaces";
import { HttpService } from "../../modules/shared/services/http.service";

// Create a new offer
export const createOffer = async (offerData: Partial<Offer>): Promise<Offer> => {
  try {
    
    const response = await HttpService.post(
      "api/v1/offers",
      offerData,
      { "Content-Type": "application/json", "x-custom-lang": "en" },
      true
    );
    return response;
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || "Failed to create offer";
    throw new Error(errorMessage);
  }
};

// Update an existing offer
export const updateOffer = async (id: string, offerData: Partial<Offer>): Promise<Offer> => {
  try {
    const response = await HttpService.patch(
      `api/v1/offers/${id}`,
      offerData,
      { "Content-Type": "application/json", "x-custom-lang": "en" },
      true
    );
    return response;
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || "Failed to update offer";
    throw new Error(errorMessage);
  }
};

// Fetch a single offer by ID
export const fetchOffer = async (id: string): Promise<Offer> => {
  try {
    const response = await HttpService.get(
      `api/v1/offers/${id}`,
      true,
      { "x-custom-lang": "en" }
    );
    return response;
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || "Failed to fetch offer";
    throw new Error(errorMessage);
  }
};

// Fetch user's offers with pagination and filters
export const fetchUserOffers = async (params: {
  page?: number;
  limit?: number;
  status?: OfferStatusEnum;
  search?: string;
  jobUserId?: string;
  isSent?: boolean;
  userId?: string;
  sort?: Array<{ orderBy: string; order: string }>;
  statusIds?: string;
  fromDate?: string;
  toDate?: string;
}): Promise<{
  data: Offer[];
  totalCount: number;
  currentPage: number;
  totalPages: number;
  hasNextPage: boolean;
}> => {
  try {
    const queryParams = new URLSearchParams();
    if (params.page) queryParams.append("page", params.page.toString());
    if (params.limit) queryParams.append("limit", params.limit.toString());
    if (params.status) queryParams.append("status", params.status.toString());
    if (params.search) queryParams.append("search", params.search);
    
    // Add the new filter structure
    if (params.jobUserId) queryParams.append("jobUserId", params.jobUserId);
    if (params.isSent !== undefined) queryParams.append("isSent", params.isSent.toString());
    if (params.userId) queryParams.append("userId", params.userId);
    if (params.sort) queryParams.append("sort", JSON.stringify(params.sort));
    if (params.statusIds) queryParams.append("statusIds", params.statusIds);
    if (params.fromDate) queryParams.append("fromDate", params.fromDate);
    if (params.toDate) queryParams.append("toDate", params.toDate);

    const response = await HttpService.get(
      `api/v1/offers?${queryParams}`,
      true,
      { "x-custom-lang": "en" }
    );
    return response;
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || "Failed to fetch offers";
    throw new Error(errorMessage);
  }
};

// Delete an offer
export const deleteOffer = async (id: string): Promise<void> => {
  try {
    await HttpService.deleteRecord(
      `api/v1/offers/${id}`,
      { "x-custom-lang": "en" },
      true
    );
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || "Failed to delete offer";
    throw new Error(errorMessage);
  }
};



// Upload file for offer
export const uploadFile = async (file: File): Promise<{ id: string; name: string; size: number; type: string }> => {
  try {
    const formData = new FormData();
    formData.append("file", file);

    const response = await HttpService.post(
      "api/v1/files/upload",
      formData,
      { "x-custom-lang": "en" },
      true
    );
    return response.data;
  } catch (error: any) {
    const errorMessage = error?.data?.message || error?.message || "Failed to upload file";
    throw new Error(errorMessage);
  }
};

// Helper function to format offer data for API
export const formatOfferForAPI = (offerData: Partial<Offer>): any => {
  return {
    timelineType: {
      id: offerData.timelineType?.id
    },
    status: {
      id: offerData.status?.id || OfferStatusEnum.Approved
    },
    files: offerData.files?.map(file => ({ id: file.id })) || [],
    isWarranty: offerData.isWarranty || false,
    isPartsIncluded: offerData.isPartsIncluded || false,
    timeline: offerData.timeline || 1,
    description: offerData.description || "",
    title: offerData.title || "",
    amount: offerData.amount || 0,
    user: {
      id: offerData.user?.id
    },
    job: {
      id: offerData.job?.id
    }
  };
};

// Helper function to validate offer data
export const validateOfferData = (offerData: Partial<Offer>): { isValid: boolean; errors: Record<string, string> } => {
  const errors: Record<string, string> = {};

  if (!offerData.title || offerData.title.length < 10) {
    errors.title = "Title must be at least 10 characters long";
  }
  if (offerData.title && offerData.title.length > 70) {
    errors.title = "Title must not exceed 70 characters";
  }

  if (!offerData.description || offerData.description.length < 15) {
    errors.description = "Description must be at least 15 characters long";
  }
  if (offerData.description && offerData.description.length > 600) {
    errors.description = "Description must not exceed 600 characters";
  }

  if (!offerData.job?.id) {
    errors.job = "Please select a job";
  }

  if (!offerData.timeline || offerData.timeline <= 0) {
    errors.timeline = "Timeline must be greater than 0";
  }

  if (!offerData.amount || offerData.amount <= 0) {
    errors.amount = "Amount must be greater than 0";
  }

  return {
    isValid: Object.keys(errors).length === 0,
    errors
  };
};
